import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DEMO_TABS, PRODUCT_SHOTS } from "@/constants/site";
import PersonalizacaoStack from "@/components/site/PersonalizacaoStack";

const EASE = [0.22, 1, 0.36, 1];

/**
 * IA Personalizada — 3-tab section highlighting personalization capabilities.
 */
export default function ProductDemo() {
  const [active, setActive] = useState(0);
  const tab = DEMO_TABS[active];
  const img = PRODUCT_SHOTS[tab.img];

  return (
    <section
      id="produto"
      data-testid="product-demo"
      className="relative env-white py-24 md:py-32 border-t hairline"
    >
      <div className="max-w-[1280px] mx-auto px-6 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end mb-12 md:mb-16">
          <div className="md:col-span-8">
            <span className="kicker">IA Personalizada</span>
            <h2 className="mt-3 font-display text-[#1A3A61] text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-[-0.02em] max-w-3xl">
              Sua IA, suas regras. Com módulos{" "}
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: "linear-gradient(90deg, #00C2D1 0%, #2EE59D 100%)" }}
              >
                personalizados para o seu Órgão.
              </span>
            </h2>
          </div>
          <div className="md:col-span-4">
            <p className="text-[#1A3A61]/65 text-[15px] leading-relaxed">
              Escolha a personalização e veja como a licito.guru se molda à
              realidade da sua instituição.
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b hairline flex flex-wrap gap-0" role="tablist">
          {DEMO_TABS.map((t, i) => (
            <button
              key={t.id}
              data-testid={`demo-tab-${t.id}`}
              role="tab"
              aria-selected={active === i}
              onClick={() => setActive(i)}
              className={`relative px-5 md:px-6 py-4 text-[14px] md:text-[15px] font-medium transition-colors ${
                active === i
                  ? "text-[#1A3A61]"
                  : "text-[#1A3A61]/50 hover:text-[#1A3A61]/80"
              }`}
            >
              <span className="flex items-center gap-2">
                <span className="text-[11px] font-mono text-[#1A3A61]/40">
                  0{i + 1}
                </span>
                {t.label}
              </span>
              {active === i && (
                <motion.span
                  layoutId="demo-tab-underline"
                  className="absolute left-0 right-0 bottom-[-1px] h-[2px] bg-[#0A1836]"
                  transition={{ duration: 0.4, ease: EASE }}
                />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mt-10 md:mt-14 items-start">
          <div className="lg:col-span-5">
            <AnimatePresence mode="wait">
              <motion.div
                key={tab.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.4, ease: EASE }}
              >
                <h3 className="font-display text-[#1A3A61] text-2xl md:text-3xl leading-[1.15] tracking-[-0.015em]">
                  {tab.title}
                </h3>
                <p className="mt-5 text-[#1A3A61]/70 text-[15px] md:text-base leading-relaxed">
                  {tab.desc}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="lg:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={tab.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.5, ease: EASE }}
                className="relative"
              >
                {tab.id === "personalizacao" ? (
                  <PersonalizacaoStack />
                ) : (
                  <div className="bg-white product-frame overflow-hidden rounded-md">
                    <div className="flex items-center gap-1.5 px-4 py-2.5 border-b hairline bg-[#F7F8FA]">
                      <span className="h-2.5 w-2.5 rounded-full bg-[#FF5F57]/70" />
                      <span className="h-2.5 w-2.5 rounded-full bg-[#FEBC2E]/70" />
                      <span className="h-2.5 w-2.5 rounded-full bg-[#28C840]/70" />
                      <span className="ml-3 text-[11px] text-[#1A3A61]/50 font-medium">
                        app.licito.guru / {tab.id}
                      </span>
                    </div>
                    <img
                      src={img}
                      alt={tab.title}
                      className="w-full h-auto block"
                      loading="lazy"
                    />
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
}
